import Image from 'next/image';
import styled from 'styled-components';
import BreadCrumbs from '@99designs/acquisition-frontend/shared/components/BreadCrumbs';
import {
    Attribution,
    AttributionLink,
    Box,
    Button,
    CTALink,
    Heading,
    Paragraph,
    Stack,
} from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors } from '@99designs/emu/settings';
import { FormattedMessage } from '@99designs/frontend-components/i18n';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { FeaturesList, FeaturesListProps } from '@99designs/marketing-ui';
import { linkTo } from '@99designs/routing';
import { Price as FromPrice } from '@99designs/spa-common';
import { trackCTAClicked, useTrackCTALinkClicked } from '../../../lib/events';
import type { ButtonColorConfig } from '@99designs/emu';
import type { HeroImage, ProductLandingGroupKey } from '../../../lib/productLandingGroups';
import type { Price } from '../../../util/price';

const Small = styled(Box)`
    display: none;
    ${mediaQuery.only('small')} {
        display: block;
    }
`;

const Medium = styled(Box)`
    display: none;
    ${mediaQuery.only('medium')} {
        display: block;
    }
`;

const Normal = styled(Box)`
    display: none;
    ${mediaQuery.only('normal')} {
        display: block;
    }
`;

const Large = styled(Box)`
    display: none;
    ${mediaQuery.minimum('large')} {
        display: block;
    }
`;

export type HeroSectionProps = {
    categoryKey: ProductLandingGroupKey;
    title: string;
    features: FeaturesListProps['features'];
    introduction: string;
    heroImage: HeroImage;
    heroCtaAppearance?: ButtonColorConfig;
    fromPrice?: Price;
    breadcrumbLabel: string;
    ctaLink: string;
    isContestFocusEnabled?: boolean;
};

export const HeroSection = ({
    categoryKey,
    title,
    features,
    fromPrice,
    introduction,
    heroImage,
    breadcrumbLabel,
    heroCtaAppearance,
    ctaLink,
    isContestFocusEnabled = false,
}: HeroSectionProps) => {
    const seePricingOptionsCTARef = useTrackCTALinkClicked({
        category: categoryKey,
        ctaLocation: 'hero section',
        ctaButton: 'See pricing options',
    });

    return (
        <Box
            display="flex"
            flexDirection={['column', 'row']}
            background={`linear-gradient(${colors.white}, ${colors.grey100})`}
        >
            <Box
                width={['inherit', '50%']}
                display="flex"
                justifyContent={['flex-start', 'flex-end']}
            >
                <Box
                    maxWidth="650px"
                    width="100%"
                    paddingLeft={['1bu', '2bu']}
                    paddingRight={['1bu', '3bu']}
                    paddingBottom={['2bu', '4bu']}
                >
                    <BreadCrumbs
                        breadcrumbs={[
                            {
                                label: __placeholder('Home'),
                                path: '/',
                            },
                            {
                                label: breadcrumbLabel,
                            },
                        ]}
                    />
                    <Box paddingTop={['3bu', '4bu']} paddingBottom="1bu">
                        <div style={{ color: isContestFocusEnabled ? '#00816E' : '#000000' }}>
                            <Heading accessibilityLevel={1} appearanceLevel={0} color="chameleon">
                                {title}
                            </Heading>
                        </div>
                    </Box>
                    <Paragraph>{introduction}</Paragraph>
                    <Box paddingTop="1bu">
                        <Stack space="0.5bu">
                            <Heading accessibilityLevel={2} appearanceLevel={5}>
                                {!isContestFocusEnabled && __('What you get')}

                                {isContestFocusEnabled && fromPrice && (
                                    <FormattedMessage
                                        message={__placeholder('Logo design from {fromPrice}')}
                                        values={{
                                            fromPrice: (
                                                <FromPrice
                                                    amount={fromPrice.amountInCents}
                                                    currency={fromPrice.currency}
                                                />
                                            ),
                                        }}
                                    />
                                )}
                            </Heading>
                            <FeaturesList features={features} />
                        </Stack>
                    </Box>
                    {!isContestFocusEnabled && fromPrice && (
                        <Box paddingTop="1bu">
                            <Heading accessibilityLevel={2} appearanceLevel={4}>
                                <FormattedMessage
                                    message={__('Starting from {fromPrice}')}
                                    values={{
                                        fromPrice: (
                                            <FromPrice
                                                amount={fromPrice.amountInCents}
                                                currency={fromPrice.currency}
                                            />
                                        ),
                                    }}
                                />
                            </Heading>
                        </Box>
                    )}
                    <Box
                        paddingTop={['1.5bu', '2bu']}
                        display="flex"
                        flexDirection={['column', 'row', 'row', 'row']}
                    >
                        <Box marginRight={['none', '2bu', '2bu', '2bu']}>
                            <Button
                                appearance={heroCtaAppearance}
                                href={ctaLink}
                                size="large"
                                type="button"
                                width={['fill', 'auto', 'auto', 'auto']}
                                ref={seePricingOptionsCTARef}
                            >
                                {isContestFocusEnabled
                                    ? __placeholder('Get started')
                                    : __('See pricing options')}
                            </Button>
                        </Box>
                        <Box
                            display="flex"
                            justifyContent={['center', 'flex-start', 'flex-start', 'flex-start']}
                            paddingTop={['1bu', '0.75bu', '0.75bu', '0.75bu']}
                            paddingBottom={['0.5bu', '1bu', '1bu', '1bu']}
                        >
                            <CTALink
                                href="#how-it-works"
                                onClick={() =>
                                    trackCTAClicked({
                                        category: categoryKey,
                                        ctaLocation: 'hero section',
                                        ctaButton: 'How it works',
                                    })
                                }
                            >
                                {__('How it works')}
                            </CTALink>
                        </Box>
                    </Box>
                </Box>
            </Box>
            <Small
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[0]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="top"
                    layout="fill"
                    sizes="100vw"
                    quality={75}
                    priority
                />
            </Small>
            <Medium
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[1]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="top"
                    layout="fill"
                    sizes="100vw"
                    quality={75}
                    priority
                />
            </Medium>
            <Normal
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[2]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="left"
                    layout="fill"
                    sizes="50vw"
                    quality={75}
                    priority
                />
            </Normal>
            <Large
                width={['inherit', '50%']}
                minHeight={['600px', 'inherit']}
                maxWidth="1000px"
                position="relative"
            >
                <Image
                    src={heroImage.sources[3]}
                    alt={heroImage.altText}
                    objectFit="cover"
                    objectPosition="left"
                    layout="fill"
                    sizes="50vw"
                    quality={75}
                    priority
                />
            </Large>
            {heroImage.attributions ? (
                <Box
                    position="absolute"
                    bottom={['1bu', '1.5bu']}
                    right={['1.5bu', '1.5bu', '1.5bu', '6bu']}
                    data-testid="hero-design-attribution"
                >
                    <HeroAttribution attributions={heroImage.attributions} />
                </Box>
            ) : null}
        </Box>
    );
};

interface HeroAttributionProps {
    attributions: NonNullable<HeroImage['attributions']>;
}
function HeroAttribution({ attributions }: HeroAttributionProps) {
    return (
        <Attribution>
            <AttributionLink href={linkTo.userProfile(attributions.id)}>
                {formatMessage(__placeholder('by {designer}'), {
                    designer: attributions.name,
                })}
            </AttributionLink>
        </Attribution>
    );
}
